import { HeroImage, HeroImageFromCollectionDeps, HeroImageResponsive } from './collection-view.interface'
import { ResponsiveSizes, ResponsiveSources } from '../../../shared/responsive-image/responsive-image.interface'
import { breakpointsInPixels } from '../../../app.config'
import * as F from 'fp-ts/function'
import { CollectionEntityType } from 'src/io/models/collection'

const SMALL_WIDTH = 600
const MEDIUM_WIDTH = 1000
const LARGE_WIDTH = 1800

/**
 * Determine whether we should redirect to new team & person routes
 * So we can have unique pages for teams and athletes with nodeId in the route
 * Otherwise we continue to go to collection/tag/slug route
 * @param type
 * @param nodeId
 * @returns true|false
 */
export const redirectToNewTeamPersonRoute = (slugUri?: string) => {
  return !!slugUri && (slugUri.includes('/people/') || slugUri.includes('/teams/'))
}

/** Given a title and url, create a HeroImage object. */
// prettier-ignore
export const createHeroImage =
  (c: HeroImageFromCollectionDeps): HeroImage | undefined => {
    const url = c.asset_url === '' ? undefined : c.asset_url
    if (url === undefined || url === null) return undefined
    return {
      url,
      alt: `picture of ${c.title}`
    }
  }

const decorateUrl = (url: string) => (size: number) => `${url}?width=${size}`

/**
 * Given a url, create a set of default responsive sources for that URL.
 *
 * This function relies on the URL accepting a parameter called "width" like
 * cloudfront urls do. Images are sized at {@link SMALL_WIDTH},
 * {@link MEDIUM_WIDTH}, and {@link LARGE_WIDTH}.
 *
 * @example
 * makeHeroImageResponsiveSources('https://d2779tscntxxsw.cloudfront.net/5c06c528040dc.png')
 */
// prettier-ignore
export const makeHeroImageResponsiveSources =
  (url: string): ResponsiveSources => {
    const getUrlForWidth = decorateUrl(url)
    return [SMALL_WIDTH, MEDIUM_WIDTH, LARGE_WIDTH].map(size => ({
      size,
      url: getUrlForWidth(size),
      unit: 'w'
    }))
  }

/**
 * Obtain default responsive sizes for Hero Images.
 *
 * @see responsiveHeroImageFromCollection
 * @see heroImageResponsiveEnrichment
 */
export const getDefaultResponsiveSizes = (): ResponsiveSizes => ({
  default: { size: 1800, unit: 'w' },
  responsiveSizes: [
    {
      mediaQuery: `(max-width: ${breakpointsInPixels.small})`,
      size: 600,
      unit: 'w'
    },
    {
      mediaQuery: `(max-width: ${breakpointsInPixels.medium})`,
      size: 1000,
      unit: 'w'
    }
  ]
})

/**
 * Add properties for use with responsive image directive to a hero image.
 *
 * @see FloResponsiveImageDirective
 */
// prettier-ignore
export const heroImageResponsiveEnrichment =
  (i: HeroImage | undefined | null): HeroImageResponsive | undefined => {
    if (i === undefined || i === null) return undefined
    return {
      ...i,
      sources: makeHeroImageResponsiveSources(i.url),
      sizes: getDefaultResponsiveSizes()
    }
  }

/**
 * Produce a responsive hero image from a collection object.
 *
 * `flow` is an fp-ts utility function that composes functions together. It
 * works the same way as an rxjs pipe.
 */
export const createResponsiveHeroImage = F.flow(createHeroImage, heroImageResponsiveEnrichment)
